import { isValid, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAppSelector } from '../../../../configuration/setup/store';
import { getIdToken } from '../../../../configuration/tokenHandling/tokenSlice';
import { loadFromLocalStorage, saveInLocalStorage } from '../../common/onboarding/handleLocalStorage';
import { type BookingOverviewFilters, emptyBookingOverviewFilters } from './bookingOverviewFilters';
import type { BookingOverviewStatus } from './bookingOverviewStatus';

type BookingOverviewUrlState = { filters: BookingOverviewFilters; search: string };

const filterParameters = ['resource', 'period', 'status', 'free', 'withoutRenewal', 'endsFrom', 'endsTo'];
export const getBookingOverviewFiltersStorageKey = (userId: string, accountId: string) =>
    `bookings.overview.filters.v1:${encodeURIComponent(userId)}:${encodeURIComponent(accountId)}`;
const isStatus = (value: unknown): value is BookingOverviewStatus =>
    value === 'ACTIVE' || value === 'PENDING' || value === 'EXPIRING' || value === 'UNASSIGNED';
const uniqueValues = (params: URLSearchParams, key: string) => [...new Set(params.getAll(key).filter(Boolean))];
const readDate = (value: string | null) =>
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value)) ? value : undefined;

export function readBookingOverviewUrlState(search: string): BookingOverviewUrlState {
    const params = new URLSearchParams(search);
    if (params.get('filters') === 'none') {
        return { filters: { ...emptyBookingOverviewFilters }, search: params.get('q') ?? '' };
    }
    return {
        filters: {
            resources: uniqueValues(params, 'resource').flatMap(value => {
                const separator = value.indexOf(':');
                return separator > 0 && separator < value.length - 1
                    ? [JSON.stringify([value.slice(0, separator), value.slice(separator + 1)])]
                    : [];
            }),
            periods: uniqueValues(params, 'period').filter(value => /^\d+:(days|months|years)$/.test(value)),
            statuses: uniqueValues(params, 'status').filter(isStatus),
            freeOnly: params.get('free') === 'true',
            withoutRenewal: params.get('withoutRenewal') === 'true',
            endsFrom: readDate(params.get('endsFrom')),
            endsTo: readDate(params.get('endsTo')),
        },
        search: params.get('q') ?? '',
    };
}

export function writeBookingOverviewUrlState(currentSearch: string, { filters, search }: BookingOverviewUrlState) {
    const params = new URLSearchParams(currentSearch);
    for (const key of [...filterParameters, 'filters', 'q']) {
        params.delete(key);
    }
    for (const resource of filters.resources) {
        const [type, id] = JSON.parse(resource) as [string, string];
        params.append('resource', `${type}:${id}`);
    }
    for (const [key, values] of [
        ['period', filters.periods],
        ['status', filters.statuses],
    ] as const) {
        for (const value of values) {
            params.append(key, value);
        }
    }
    if (filters.freeOnly) {
        params.set('free', 'true');
    }
    if (filters.withoutRenewal) {
        params.set('withoutRenewal', 'true');
    }
    if (filters.endsFrom) {
        params.set('endsFrom', filters.endsFrom);
    }
    if (filters.endsTo) {
        params.set('endsTo', filters.endsTo);
    }
    if (!filterParameters.some(key => params.has(key))) {
        params.set('filters', 'none');
    }
    if (search) {
        params.set('q', search);
    }
    return params.toString();
}

function getInitialUrlState(location: { key: string; search: string }, storageKey?: string, useUrlState = true) {
    const params = new URLSearchParams(location.search);
    // Explicit links describe the complete selection, including an explicitly unfiltered view.
    const hasUrlState = params.get('filters') === 'none' || [...filterParameters, 'q'].some(key => params.has(key));
    const stored: unknown = storageKey && (!useUrlState || !hasUrlState) ? loadFromLocalStorage(storageKey) : undefined;
    const state = { filters: emptyBookingOverviewFilters, search: '' };
    if (typeof stored === 'string') {
        state.filters = readBookingOverviewUrlState(stored).filters;
    }
    return {
        storageKey,
        key: location.key,
        originalSearch: location.search,
        search:
            useUrlState && (hasUrlState || typeof stored !== 'string')
                ? location.search.replace(/^\?/, '')
                : writeBookingOverviewUrlState(location.search, state),
    };
}

export function useBookingOverviewUrlState() {
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAppSelector(getIdToken);
    const storageKey =
        token?.sub && token.account ? getBookingOverviewFiltersStorageKey(token.sub, token.account) : undefined;
    const [initial, setInitial] = useState(() => getInitialUrlState(location, storageKey));

    // Identity can arrive after mount or change without remounting this page. Restore the new
    // scope before effects can persist the previous account's selection under its storage key.
    if (storageKey && initial.storageKey !== storageKey) {
        setInitial(getInitialUrlState(location, storageKey, initial.storageKey === undefined));
    }
    const isInitialLocation = initial.key === location.key && initial.originalSearch === location.search;
    const isScopeReady = storageKey !== undefined && initial.storageKey === storageKey;
    const needsRestore = isScopeReady && isInitialLocation && initial.search !== location.search.replace(/^\?/, '');
    const state = readBookingOverviewUrlState(isInitialLocation ? initial.search : location.search);
    const filtersValue = writeBookingOverviewUrlState('', { filters: state.filters, search: '' });

    useEffect(() => {
        if (needsRestore) {
            navigate(
                { search: initial.search, hash: location.hash },
                { replace: true, state: location.state, preventScrollReset: true }
            );
        }
    }, [initial.search, needsRestore, location.hash, location.state, navigate]);

    useEffect(() => {
        if (isScopeReady && !needsRestore) {
            saveInLocalStorage(storageKey, filtersValue);
        }
    }, [storageKey, isScopeReady, filtersValue, needsRestore]);

    const update = (nextState: BookingOverviewUrlState, replace = false) => {
        const search = writeBookingOverviewUrlState(location.search, nextState);
        if (search !== location.search.replace(/^\?/, '')) {
            navigate({ search, hash: location.hash }, { replace, state: location.state, preventScrollReset: true });
        }
    };

    return {
        ...state,
        setFilters: (filters: BookingOverviewFilters) => update({ ...state, filters }),
        // Search uses replace so each keystroke does not create a history entry.
        setSearch: (search: string) => update({ ...state, search }, true),
        clearFilters: () => update({ filters: emptyBookingOverviewFilters, search: '' }),
    };
}
